import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { IconX } from '@tabler/icons-react';
import { changePassword } from '../services/users';
import { MIN_PASSWORD_LENGTH } from '../constants/auth';
import {
  ModalOverlay,
  ModalCard,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  FormGroup,
  Label,
  Input,
  HelperText,
  ErrorAlert,
  AlertBanner,
  Button,
  H2Title,
} from './styled';

interface ChangePasswordModalProps {
  onClose: () => void;
}

function ChangePasswordModal({ onClose }: ChangePasswordModalProps) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const mutation = useMutation({
    mutationFn: () => changePassword(currentPassword, newPassword),
    onSuccess: () => {
      setSuccess(true);
      setError(null);
    },
    onError: (err: any) => {
      setError(err.response?.data?.error || 'Failed to change password');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (newPassword.length < MIN_PASSWORD_LENGTH) {
      setError(`New password must be at least ${MIN_PASSWORD_LENGTH} characters`);
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      return;
    }
    if (newPassword === currentPassword) {
      setError('New password must be different from your current password');
      return;
    }

    mutation.mutate();
  };

  return (
    <ModalOverlay onClick={onClose}>
      <ModalCard onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <H2Title>Change Password</H2Title>
          <ModalCloseButton onClick={onClose} aria-label="Close">
            <IconX size={18} stroke={2} />
          </ModalCloseButton>
        </ModalHeader>
        {success ? (
          <>
            <ModalBody>
              <AlertBanner variant="success">Your password has been updated.</AlertBanner>
            </ModalBody>
            <ModalFooter>
              <Button type="button" onClick={onClose}>Done</Button>
            </ModalFooter>
          </>
        ) : (
          <form onSubmit={handleSubmit}>
            <ModalBody>
              {error && <ErrorAlert>{error}</ErrorAlert>}
              <FormGroup>
                <Label>Current Password</Label>
                <Input
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  autoComplete="current-password"
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label>New Password</Label>
                <Input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  autoComplete="new-password"
                  minLength={MIN_PASSWORD_LENGTH}
                  required
                />
                <HelperText>At least {MIN_PASSWORD_LENGTH} characters.</HelperText>
              </FormGroup>
              <FormGroup>
                <Label>Confirm New Password</Label>
                <Input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  autoComplete="new-password"
                  required
                />
              </FormGroup>
            </ModalBody>
            <ModalFooter>
              <Button variant="secondary" type="button" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={mutation.isPending}>
                {mutation.isPending ? 'Saving...' : 'Update Password'}
              </Button>
            </ModalFooter>
          </form>
        )}
      </ModalCard>
    </ModalOverlay>
  );
}

export default ChangePasswordModal;
